import _ from 'lodash';
import config from 'config';

import facebook from './facebook';
import googleAnalytics from './google-analytics';
import googleRemarketing from './google-remarketing';
import adroll from './adroll';
import custom from './custom';
import perfectAudience from './perfect-audience';
import pinterest from './pinterest';
import twitter from './twitter';

let instance = null;

export default class Analytics {
  constructor() {
    if (!instance) instance = this;

    this.vsInit = false;
    this.googleRemarketingInterval = null;
    this.pendingEvents = [];

    return instance;
  }

  pageView(to) {
    if (config.Analytics.enabled) {
      this._loadVSTags();
      if (to.name.indexOf('sales') === -1 && to.name.indexOf('checkout') === -1 && to.name !== 'thank-you' && to.name !== 'client-store') this._default();
      this._vwo(to);
    }
  }

  sales(campaign, product, coupon = null) {
    if (config.Analytics.enabled) this._vsSales(campaign, product, coupon);
    if (config.ClientAnalytics.enabled) this._clientSales(campaign);
  }

  thankYou(order, upsellOrder) {
    if (config.Analytics.enabled && !upsellOrder) this._vsThankYou(order);
    if (config.ClientAnalytics.enabled) this._clientThankYou(order, upsellOrder);
  }

  store(store, storeUrl) {
    if (config.Analytics.enabled) this._vsStore();
    if (config.ClientAnalytics.enabled) this._clientStore(store, storeUrl);
  }

  addToCart(campaign, product, quantity, size) {
    if (config.Analytics.enabled) this._vsAddToCart(campaign, product, quantity, size);
    if (config.ClientAnalytics.enabled) this._clientAddToCart(campaign);
  }

  initiateCheckout(campaign, numItems) {
    if (config.Analytics.enabled) this._vsInitiateCheckout(campaign, numItems);
    if (config.ClientAnalytics.enabled) this._clientInitiateCheckout(campaign, numItems);
  }

  checkout({ cart, upsell, option }) {
    if(config.Analytics.enabled) this._vsCheckout(cart, upsell, option);
  }

  placeOrder({ cart, upsell, option }) {
    if (config.Analytics.enabled) this._vsPlaceOrder(cart, upsell, option);
  }

  removeFromCart(product, quantity, size) {
    if (config.Analytics.enabled) {
      googleAnalytics.removeFromCart(product, quantity, size);

      window.dataLayer.push({ 'event': 'remove-from-cart-tracking' });
    }
  }

  changeSize(size) {
    if (config.Analytics.enabled) {
      googleAnalytics.changeSize(size);

      window.dataLayer.push({ 'event': 'change-size-tracking' });
    }
  }

  changeQuantity(quantity) {
    if (config.Analytics.enabled) {
      googleAnalytics.changeQuantity(quantity);

      window.dataLayer.push({ 'event': 'change-quantity-tracking' });
    }
  }

  changeColor(color) {
    if (config.Analytics.enabled) {
      googleAnalytics.changeColor(color);

      window.dataLayer.push({ 'event': 'change-color-tracking' });
    }
  }

  applyCoupon(coupon) {
    if (config.Analytics.enabled) {
      googleAnalytics.applyCoupon(coupon);

      window.dataLayer.push({ 'event': 'apply-coupon-tracking' });
    }
  }

  register() {
    if (config.Analytics.enabled) {
      googleAnalytics.register();
      googleRemarketing.register();

      this._asyncEvent('register-tracking');
    }
  }

  _loadVSTags() {
    if (!this.vsInit) {
      window.dataLayer.push({ 'event': 'load-vs-tags' });
      this.vsInit = true;
    }
  }

  _default() {
    facebook.default();
    googleAnalytics.default();
    googleRemarketing.default();

    this._asyncEvent('vs-default-tracking');
  }

  _vsSales(campaign, product, coupon) {
    if (!campaign.pixels.global.disable_vs_retargeting) {
      facebook.vsSales(campaign);
      googleRemarketing.vsSales(campaign);
    }
    googleAnalytics.vsSales(campaign, product, coupon);
  }

  _clientSales(campaign) {
    adroll.clientSales(campaign);
    facebook.clientSales(campaign);
    googleRemarketing.clientSales(campaign);
    perfectAudience.clientSales(campaign);

    googleAnalytics.clientSales(campaign);
  }

  _vsThankYou(order) {
    if (!order.campaign.pixels.global.disable_vs_retargeting) {
      facebook.vsThankYou(order);
      googleRemarketing.vsThankYou(order);
    }
    googleAnalytics.vsThankYou(order);
  }

  _clientThankYou(order, upsellOrder) {
    if (!upsellOrder) {
      adroll.clientThankYou(order);
      custom.clientThankYou(order);
      facebook.clientThankYou(order);
      googleRemarketing.clientThankYou(order);
      perfectAudience.clientThankYou(order);
      pinterest.clientThankYou(order);
      twitter.clientThankYou(order);
    }

    googleAnalytics.clientThankYou(order, upsellOrder);
  }

  _vsStore() {
    this._default();
  }

  _clientStore(store, storeUrl) {
    facebook.clientStore(store, storeUrl);

    googleAnalytics.clientStore(store);
  }

  _vsAddToCart(campaign, product, quantity, size) {
    if (!campaign.pixels.global.disable_vs_retargeting) facebook.vsAddToCart(campaign);
    googleAnalytics.addToCart(product, quantity, size);

    window.dataLayer.push({ 'event': 'vs-add-to-cart-tracking' });
  }

  _clientAddToCart(campaign) {
    facebook.clientAddToCart(campaign);
  }

  _vsInitiateCheckout(campaign, numItems) {
    if (!campaign.pixels.global.disable_vs_retargeting) {
      facebook.vsInitiateCheckout(campaign, numItems);

      window.dataLayer.push({ 'event': 'vs-initiate-checkout-tracking' });
    }
  }

  _vsCheckout(cart, upsell, option) {
    googleAnalytics.checkout(cart, upsell, option);

    window.dataLayer.push({ 'event': 'vs-checkout-tracking' });
  }

  _clientInitiateCheckout(campaign, numItems) {
    facebook.clientInitiateCheckout(campaign, numItems);
  }

  _vsPlaceOrder(cart, upsell, option) {
    googleAnalytics.placeOrder(cart, upsell, option);

    window.dataLayer.push({ 'event': 'vs-place-order-tracking' });
  }

  _vwo(to) {
    if (config.VWO.enabled) {
      let url;
      switch (to.name) {
        // uncomment to enable store
        // case 'sales-store':
        //   url = `https://viralstyle.com/salespage2${location.pathname}`;
        //   break;
        // uncomment to enable checkout
        // case 'checkout':
        //   url = `https://viralstyle.com/checkout2`;
        //   break;
        case 'thank-you':
          url = `https://viralstyle.com/thankyou2`;
          break;
      }
      if (url) {
        window.dataLayer.push({ 'vwoUrl': url });
        window.dataLayer.push({ 'vwoId': config.VWO.id });
        window.dataLayer.push({ 'event': 'vwo' });
      }
    }
  }

  _asyncEvent(event) {
    if (window.google_trackConversion) {
      window.dataLayer.push({ event });
    } else {
      // async tags -__-
      this.pendingEvents.push(event);
      if(!this.googleRemarketingInterval) {
        this.googleRemarketingInterval = setInterval(() => {
          if (window.google_trackConversion) {
            clearInterval(this.googleRemarketingInterval);
            this.googleRemarketingInterval = null;
            _.each(this.pendingEvents, pendingEvent => window.dataLayer.push({ event: pendingEvent }));
            this.pendingEvents = [];
          }
        }, 100);
      }
    }
  }
}


// WEBPACK FOOTER //
// ./source/client/analytics/index.js